// Support's content frame and its header actions. The workspace chrome (top nav, brand
// mark, theme toggle, user chip, sign-out and the dev-stub warning) moved to the platform
// shell (web/shell/PlatformHeader.jsx and PlatformLayout.jsx), and the drafts tab row is
// handed to it as Support's BelowHeader (support-module/entry.jsx). What stays here is
// Support's own: the content WIDTH per route, and the header's right-hand controls with
// the feature-request board they open.
//
// Colours come from semantic tokens (bg-surface, text-ink, border-line — see the note
// at the top of src/styles/app.css), so these controls flip theme with the shell.

import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

import { useCanWrite } from '../session/SessionProvider.jsx';
import SearchForm from '../components/SearchForm.jsx';
import Board from '../components/feature-requests/Board.jsx';
import { stripSupportBase, supportPath } from '../support-path.js';

// Routes that render a DATA TABLE rather than prose, and so should use the whole
// monitor instead of the reading width.
//
// docs/ADMIN-CONSOLE-PLAN.md §2 fix #2 is explicit about why this is a per-route
// decision and not a global one: on a 1920px monitor roughly 704px of every screen
// was empty margin while the queue's ten columns truncated mid-word — but the same
// max-w-7xl is the RIGHT width for ticket detail, where a message body spanning
// 1900px is worse to read, not better. So the default stays the reading width and
// exactly the routes below opt out. The create screen (`/tickets/drafts/:draftId`, which
// `/tickets/new` becomes) and `/tickets/:publicRef` are deliberately absent.
//
// Matched here rather than passed up from the page, because <Outlet /> renders the
// page BELOW this element: a page cannot widen a container it is already inside
// without lifting state into the layout and re-rendering the whole shell on every
// navigation. Both class names are written out as complete literals so Tailwind's
// source scanner still sees them.
// The paths are INSIDE Support (support-path.js): the comparison strips the /support prefix
// from the address first, so this list does not name the prefix.
const WIDE_CONTENT_ROUTES = ['/tickets'];

function SearchIcon() {
  return (
    <svg viewBox="0 0 20 20" className="size-5" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
      />
    </svg>
  );
}

// A light bulb, for "an idea": the feature-request button's face below xl. Not a second
// "+", which "+ New ticket" beside it already is.
function IdeaIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 20 20" className={`size-5 ${className}`} fill="currentColor" aria-hidden="true">
      <path d="M10 1a6 6 0 0 0-3.815 10.631C7.237 12.5 8 13.443 8 14.456v.644a.75.75 0 0 0 .572.729 6.016 6.016 0 0 0 2.856 0A.75.75 0 0 0 12 15.1v-.644c0-1.013.762-1.957 1.815-2.825A6 6 0 0 0 10 1ZM8.863 17.414a.75.75 0 0 0-.226 1.483 9.066 9.066 0 0 0 2.726 0 .75.75 0 0 0-.226-1.483 7.553 7.553 0 0 1-2.274 0Z" />
    </svg>
  );
}

/**
 * The way into search from the top bar (components/SearchForm.jsx does the searching):
 * a box where the bar has room for one, and otherwise a search icon linking to /search,
 * whose page has a box of its own. So from sm up, wherever the nav links show, search
 * is one click away. Below sm the bar drops the nav links, and this slot with them.
 *
 * No icon on /search itself, where the page's box is already on screen; the slot keeps
 * its width there, so the bar does not shift between routes.
 */
function HeaderSearch() {
  const { pathname } = useLocation();
  return (
    // The box takes only the room the bar has LEFT, up to 14rem, and is not shown where
    // less than 6rem is left; the icon takes its place. `flex-1` sizes this wrapper from
    // the free space, `@container` (size containment) stops the box inside from claiming
    // any width of its own, and `min-w-9` keeps the icon's 36px, so the icon never
    // overlaps the nav links. That 36px, plus the group's 12px gap, is what the slot
    // always costs. A fixed 14rem box, tried first, overflowed the bar at every width up
    // to 1024px. How wide the bar is at each width is the action group's story, in
    // AppLayout below.
    <div className="@container hidden min-w-9 max-w-56 flex-1 sm:block">
      <SearchForm id="header-search" label="Search tickets" className="hidden @min-[6rem]:block" />
      {stripSupportBase(pathname) !== '/search' && (
        <Link
          to={supportPath('/search')}
          aria-label="Search tickets"
          title="Search tickets"
          className="ms-auto grid size-9 place-items-center rounded-md text-ink-secondary
                     transition-colors hover:bg-sunken hover:text-ink
                     focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400
                     @min-[6rem]:hidden"
        >
          <SearchIcon />
        </Link>
      )}
    </div>
  );
}

/**
 * Support's content frame, the layout route every Support page renders inside.
 * The header, theme and sign-out moved to the platform shell (web/shell/); what stays
 * is Support's own decision about content WIDTH, per route (WIDE_CONTENT_ROUTES).
 */
export default function AppLayout() {
  const { pathname } = useLocation();
  // Compared through stripSupportBase, as before the shell: a plain `pathname`
  // comparison would never match '/tickets' under /support, and the queue would
  // silently lose its wide layout.
  // 120rem = 1920px, the widest common desktop panel — so the queue fills a 1920
  // monitor edge to edge (minus the same lg:px-8 buffer the header uses) and still
  // stops growing on an ultrawide, where a 3440px-wide table would be unreadable.
  const contentWidth = WIDE_CONTENT_ROUTES.includes(stripSupportBase(pathname)) ? 'max-w-[120rem]' : 'max-w-7xl';
  return (
    // Content, unlike the chrome above it, IS measured — see WIDE_CONTENT_ROUTES.
    <main className={`mx-auto ${contentWidth} px-4 py-6 sm:px-6 lg:px-8`}>
      <Outlet />
    </main>
  );
}

/**
 * Support's right-hand header controls, handed to the shell as `HeaderActions`
 * (web/src/support-module/entry.jsx). Same order as before the shell, by the owner's
 * 2026-10-14 order: "+ Feature Request", then search, then "+ New ticket". The board's
 * open state and the kept text of an unfinished request live HERE, so closing the board
 * to look elsewhere does not lose it; this component stays mounted for as long as
 * Support is the active module.
 *
 * The shell's action group (web/shell/PlatformHeader.jsx) renders these first, then the
 * theme toggle and the user chip, in the same `flex flex-1 items-center justify-end gap-3`
 * group they sat in before.
 */
export function SupportHeaderActions() {
  const canWrite = useCanWrite();
  const [boardOpen, setBoardOpen] = useState(false);
  // A feature request's text kept by closing its form after a filing whose outcome is
  // unknown (RequestForm.jsx). Held here, not in the board, so closing the board to look
  // elsewhere does not lose it; a reload does.
  const [keptFeatureRequest, setKeptFeatureRequest] = useState(null);
  return (
    <>
      {/* Moved with these controls from the old header; "the group" is now the shell's
          action group they render into (web/shell/PlatformHeader.jsx), which keeps the
          same classes.

          `flex-1 justify-end`: the group spans the space the brand and nav links
          leave, so the search box (HeaderSearch) can grow into it, and the
          controls still sit against the right edge. No `min-w-0`, on purpose:
          the group never narrows below the least its controls can wrap to, so
          where even that does not fit they spill to the right as they always
          have, not leftwards over the nav links, which is where a flex-end
          group's overflow goes.

          WIDTHS. Neither button in the group may wrap (`whitespace-nowrap`). Without
          it, "+ New ticket" was measured in Chrome breaking onto two lines up to
          about 935px, before "+ Feature Request" joined the group; the review of
          that addition reported both buttons wrapping up to about 1100px and the
          bar overflowing, clipping the user chip, below about 925px. So "+ Feature
          Request" shows its words only from xl (1280px), and below xl it is a
          36px icon button. THIS LAYOUT HAS NOT BEEN MEASURED IN A BROWSER: where,
          if anywhere from 640px up, the group still overflows is not known. */}

      {/* First in the group (the owner's order, 2026-10-14): "+ Feature Request",
          then search, then "+ New ticket". For every authenticated user,
          viewers included — unlike "+ New ticket"
          below. Filing and voting take no can_write (a deliberate exception,
          docs/API.md §1.8 and §11), so nothing this opens 403s for a viewer;
          moving a card is the one admin-only act, and the board draws those
          controls for admins alone. Secondary styling, so the primary action
          stays primary. Below xl it is the light bulb alone (see WIDTHS above),
          which is why the aria-label and the title are there at every width. */}
      <button
        type="button"
        aria-label="Feature request"
        title="Feature request"
        className="btn-ghost whitespace-nowrap px-2 ring-1 ring-line-strong xl:px-3"
        onClick={(event) => {
          // The board's Modal returns focus to whatever has it when it opens,
          // and not every browser focuses a button on click (Safari does not).
          event.currentTarget.focus();
          setBoardOpen(true);
        }}
      >
        <IdeaIcon className="xl:hidden" />
        <span aria-hidden="true" className="hidden xl:inline">+</span>
        <span className="hidden xl:inline">Feature Request</span>
      </button>

      {/* Not gated like "+ New ticket": every authenticated user, viewers
          included, may read every ticket (docs/API.md §1.8). */}
      <HeaderSearch />

      {/* Hidden from viewers because the create form would only fail at the
          server for them. That is presentation — POST /api/tickets still
          requires can_write, and so it should (docs/API.md §1.8). */}
      {canWrite && (
        <Link to={supportPath('/tickets/new')} className="btn-primary whitespace-nowrap">
          <span aria-hidden="true">+</span> New ticket
        </Link>
      )}

      {/* Opened from the header, not routed to: the board is a dialog over whatever page
          is open, so somebody can file the gap they just hit without leaving the ticket
          they hit it on. Modal portals it to <body>, and on close it hands focus back
          to the header button. */}
      {boardOpen && (
        <Board
          onClose={() => setBoardOpen(false)}
          kept={keptFeatureRequest}
          onKeptChange={setKeptFeatureRequest}
        />
      )}
    </>
  );
}
